import {
  Avatar,
  AvatarFallback,
  Button,
  getInitials,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useSidebar,
} from '@/components/health-ui';
import { Link, useRouterState } from '@tanstack/react-router';
import { OMNIWOUND_ROLES } from '@omniwound/auth-contract';
import { useTranslation } from 'react-i18next';

import {
  isAdminPrincipal,
  navGroupsFor,
  resolveActivePath,
} from '@/components/app-nav-items';
import { ThemeToggle } from '@/components/theme-toggle';
import { useNavEligibility } from '@/hooks/use-nav-eligibility';
import { useAuth } from '@/lib/auth-guards';
import { LogOut } from 'lucide-react';
import { createTestIdHelper } from '@/lib/utils';

const AppSidebarTestId = createTestIdHelper('app-sidebar');
const SidebarTestId = createTestIdHelper('sidebar');

/**
 * Gap between a nav button and its tooltip. The buttons sit inside
 * `SidebarContent`'s 16px gutter, so this clears that gutter and the rail's
 * border to float the tooltip just past the sidebar edge.
 */
const NAV_TOOLTIP_OFFSET_PX = 26;

/**
 * Radix reports `delayed-open`/`instant-open`/`closed`; Presence keeps the
 * content mounted while the exit animation runs (see tailwind.config.js).
 */
const NAV_TOOLTIP_MOTION =
  'data-[state=delayed-open]:animate-tooltip-in-right data-[state=instant-open]:animate-tooltip-in-right data-[state=closed]:animate-tooltip-out-right';

/**
 * Desktop sidebar. Hidden below `lg`, where the bottom tab bar takes over —
 * `cn()` is tailwind-merge, so `hidden` displaces the component's base `flex`
 * while `lg:flex` survives as a separate variant.
 */
export function AppSidebar(): React.JSX.Element {
  const { t } = useTranslation('common');
  const { logout, user, realmRoles } = useAuth();
  const { open: isSidebarOpen } = useSidebar();
  const activePath = useRouterState({
    select: (state) =>
      resolveActivePath(
        state.location.pathname,
        (state.location.search as { from?: string }).from,
      ),
  });
  const displayName =
    user?.fullName ??
    user?.email ??
    user?.preferredUsername ??
    user?.username ??
    t('shell.defaultUser');
  const isAdmin = isAdminPrincipal(realmRoles);
  // Which workspaces this account may be shown. One derivation decides the
  // sidebar, the tab bar, the QA route and the in-QA links (ADR 0328).
  const eligibility = useNavEligibility();
  const navGroups = navGroupsFor(eligibility);
  const roleLabel = isAdmin
    ? t('shell.roleAdmin')
    : realmRoles.includes(OMNIWOUND_ROLES.PROVIDER)
      ? t('shell.roleProvider')
      : t('shell.roleSignedIn');

  return (
    <Sidebar
      className="group hidden data-[state=closed]:w-[84px] data-[state=closed]:border-r lg:flex"
      data-testid={AppSidebarTestId()}
    >
      <SidebarHeader className="p-3 group-data-[state=closed]:justify-center">
        <div className="flex min-w-0 flex-1 items-center gap-2.5 group-data-[state=closed]:flex-col">
          <img
            src="/logo-mark.svg"
            alt={t('shell.appName')}
            className="size-8 shrink-0 rounded-lg"
          />
          <div className="flex min-w-0 flex-1 flex-col group-data-[state=closed]:hidden">
            <span className="font-display text-ui font-semibold">
              {t('shell.appName')}
            </span>
            <span className="text-label text-muted-foreground">
              Wound care platform
            </span>
          </div>
          <SidebarTrigger
            className="ml-auto size-8 shrink-0 group-data-[state=closed]:ml-0"
            data-testid={SidebarTestId('toggle')}
          />
        </div>
      </SidebarHeader>
      <SidebarContent>
        {navGroups.map((group) => (
          <SidebarGroup key={group.id}>
            {/* The collapsed rail spaces icons a full step apart (56px button
                + 8px gap) so the icon column breathes like a dock. */}
            <SidebarMenu className="group-data-[state=closed]:gap-2">
              {group.items.map((item) => (
                <SidebarMenuItem key={item.to}>
                  {/* The collapsed rail shows icons only, so the label moves
                      into a hover/focus tooltip; expanded, the label is
                      already on screen, so the content is hidden. The Tooltip
                      stays uncontrolled: toggling `open` between a value and
                      undefined makes Radix warn on every sidebar toggle. */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <SidebarMenuButton
                        asChild
                        isActive={activePath.startsWith(item.to)}
                        className="h-11 text-ui data-[active=true]:border-l-4 data-[active=true]:border-l-brand-orange [&_svg]:size-5 group-data-[state=closed]:h-14 group-data-[state=closed]:justify-center group-data-[state=closed]:px-0 group-data-[state=closed]:[&_svg]:size-6"
                        data-testid={SidebarTestId(`nav-${item.id}`)}
                      >
                        <Link to={item.to}>
                          <item.icon />
                          <span className="group-data-[state=closed]:sr-only">
                            {t(item.labelKey)}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </TooltipTrigger>
                    <TooltipContent
                      hidden={isSidebarOpen}
                      side="right"
                      sideOffset={NAV_TOOLTIP_OFFSET_PX}
                      className={NAV_TOOLTIP_MOTION}
                      data-testid={SidebarTestId(`nav-${item.id}-tooltip`)}
                    >
                      {t(item.labelKey)}
                    </TooltipContent>
                  </Tooltip>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter className="border-t border-border p-3 group-data-[state=closed]:justify-center">
        <div className="flex min-w-0 flex-1 items-center gap-2.5 group-data-[state=closed]:flex-col">
          <Avatar size="sm">
            <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 flex-col group-data-[state=closed]:hidden">
            <span className="truncate text-[13px] font-semibold">
              {displayName}
            </span>
            <span className="truncate text-[11px] text-muted-foreground">
              {roleLabel}
            </span>
          </div>
          <ThemeToggle />
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('nav.signOut')}
            className="size-8 shrink-0"
            data-testid={SidebarTestId('sign-out')}
            onClick={() => void logout()}
          >
            <LogOut className="size-4" />
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
